// src/mobile_pages/process/Milestone.jsx

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getMilestoneSettings, saveMilestoneSettings } from "../../api/process";
import MilestoneSelector from "../../components/process/MilestoneSelector";
import { Info, Target, Save, CheckCircle2 } from "lucide-react";

const Milestone = () => {
  const { id: projectId } = useParams();

  const [milestones, setMilestones] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchSettings = async () => {
      try {
        const settings = await getMilestoneSettings(projectId);
        if (!isMounted || !settings) return;

        const allMilestones = Array.isArray(settings.all_milestones)
          ? settings.all_milestones
          : [];
        const visibleIds = Array.isArray(settings.visible_milestone_ids)
          ? settings.visible_milestone_ids
          : [];

        setMilestones(allMilestones);
        setSelected(visibleIds);
      } catch (error) {
        console.error("마일스톤 설정 불러오기 실패:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSettings();
    return () => { isMounted = false; };
  }, [projectId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveMilestoneSettings(projectId, { visible_milestone_ids: selected });
      alert("마일스톤 표시 설정이 저장되었습니다.");
    } catch (error) {
      console.error("마일스톤 설정 저장 실패:", error);
      alert("저장 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mobile-page-root mb-16">
        <div className="mobile-page-root-inner space-y-3 p-4">
          <div className="bg-white rounded-lg p-6 animate-pulse border">
            <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
            <div className="h-4 bg-gray-100 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-100 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-4 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">마일스톤 관리</h2>
              <p className="text-gray-500 text-xs mt-0.5">
                {selected.length}개 선택됨 / 총 {milestones.length}개
              </p>
            </div>
          </div>
        </div>

        {/* 안내 카드 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Info className="w-4 h-4 text-blue-600" />
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-blue-800 leading-relaxed">
                공정표에서 <span className="font-semibold">굵은 막대(마일스톤)</span>로 
                표시할 활동을 선택하세요.
              </p>
              <p className="text-xs text-blue-700">
                선택된 활동만 주요 마일스톤으로 표시됩니다.
              </p>
            </div>
          </div>
        </div>

        {/* 선택된 마일스톤 미리보기 */}
        {selected.length > 0 && (
          <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium text-gray-700">선택된 마일스톤</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {selected.slice(0, 5).map((id) => {
                const milestone = milestones.find(m => m.id === id);
                return milestone ? (
                  <span
                    key={id}
                    className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium truncate max-w-[150px]"
                  >
                    {milestone.name}
                  </span>
                ) : null;
              })}
              {selected.length > 5 && (
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                  +{selected.length - 5}개 더
                </span>
              )}
            </div>
          </div>
        )}

        {/* 마일스톤 선택기 */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <span className="text-sm font-medium text-gray-700">마일스톤 선택</span>
          </div>
          <div className="p-4">
            <MilestoneSelector
              allMilestones={milestones}
              selectedIds={selected}
              onChange={setSelected}
            />
          </div>
        </div>

        {/* 저장 버튼 */}
        <div className="sticky bottom-4">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn-save w-full flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Save className="w-4 h-4" />
            {saving ? "저장 중..." : "변경사항 저장"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Milestone;
